import {
  ColumnFactory,
  createIconCellFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  RemoteTypeMappings,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DatabaseTableOrderField, RemoteType } from '../../../gen-src/queries/globalTypes';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { GlobalAssetGridDC3ColumnFactoryParams } from '../sorting/makeColumnFactoryParams';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';

export interface VirtualConnectionTableColumnsGridRowData {
  readonly virtualConnectionTableId: string;
  readonly id: string;
  readonly name: string | null;
  readonly remoteType: RemoteType;
  readonly maxDownstreamDatasources?: number | null;
  readonly maxDownstreamWorkbooks?: number | null;
}

type VirtualConnectionTableColumnsColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<TablesValidSortFields>,
  VirtualConnectionTableColumnsGridRowData,
  TablesValidSortFields
>;

const columns: ReadonlyArray<VirtualConnectionTableColumnsColumnFactory> = [
  () => createSelectionColumn(),
  (sortParams) => ({
    name: 'type',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      ({ remoteType }) => <RemoteTypeIcon remoteType={remoteType} />,
      ({ remoteType }) => RemoteTypeMappings[remoteType](),
    ),
    resizable: false,
    sortable: false,
  }),
  (sortParams) => ({
    name: 'name',
    dataKey: DatabaseTableOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createQueryPreservingReplacingLinkCellFormatter<VirtualConnectionTableColumnsGridRowData>(
      ({ name }) => name || '',
      (urls, { virtualConnectionTableId, id }) => urls.virtualConnectionTableLineageFieldPath(virtualConnectionTableId, id),
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'workbooks',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
    headerText: Messages.GridHeaderWorkbooks(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createNumberCellFormatter(
      ({ maxDownstreamWorkbooks }) =>
        maxDownstreamWorkbooks
          ? maxDownstreamWorkbooks
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'datasources',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
    headerText: Messages.GridHeaderDatasources(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createNumberCellFormatter(
      ({ maxDownstreamDatasources }) =>
        maxDownstreamDatasources
          ? maxDownstreamDatasources
          : null
    ),
    sortable: true,
  }),
];

export type VirtualConnectionTableColumnsDisplayGridProps =
  UsualDisplayGridProps<VirtualConnectionTableColumnsGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<TablesValidSortFields>;

export function VirtualConnectionTableColumnsDisplayGrid(props: VirtualConnectionTableColumnsDisplayGridProps): JSX.Element {
  const cols = columns.map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
